import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Check,
  Users,
  CreditCard,
  BarChart3,
  Shield,
  Zap,
  Globe,
  Menu,
  X,
  FileText,
  Palette,
  Mail,
  BookOpen,
  Layers,
  PieChart,
  Bell,
  Settings,
  ShoppingCart,
  MessageSquare,
  TrendingUp,
  Calendar,
  Bot,
  Sparkles,
  Clock,
  Webhook,
  Key,
  UserPlus,
  Building2,
  Headphones,
  FolderOpen,
  RefreshCw,
  Target,
  Award,
  ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MorphingNavbar from '../../components/MorphingNavbar';

// ============================================
// FEATURE CATEGORIES
// ============================================

const FEATURE_SECTIONS = [
  {
    id: 'client-portal',
    category: 'FOR YOUR CLIENTS',
    title: 'A Professional Portal Your Clients Will Love',
    description: 'Give your clients a branded, self-service experience that makes you look professional and saves you hours of back-and-forth.',
    gradient: 'from-orange-500 to-pink-500',
    features: [
      {
        icon: Users,
        title: 'Self-Service Dashboard',
        description: 'Clients can view their active services, check order history, and manage their account without contacting you.',
        benefit: 'Reduces support requests by 60%'
      },
      {
        icon: ShoppingCart,
        title: 'Easy Service Purchase',
        description: 'Browse your service catalog, add to cart, and checkout with Stripe. No phone calls or invoices needed.',
        benefit: 'Faster sales cycle'
      },
      {
        icon: BookOpen,
        title: 'Knowledge Base Access',
        description: 'Searchable help articles, FAQs, and tutorials so clients can find answers instantly.',
        benefit: '24/7 self-service support'
      },
      {
        icon: Bell,
        title: 'Renewal Reminders',
        description: 'Automatic notifications before services expire. Clients renew on time, you maintain revenue.',
        benefit: 'Higher retention rates'
      },
      {
        icon: FileText,
        title: 'Order History & Receipts',
        description: 'Complete purchase history with downloadable receipts for accounting.',
        benefit: 'Professional record-keeping'
      },
      {
        icon: Headphones,
        title: 'Support Ticket System',
        description: 'Clients can submit support requests, track status, and communicate directly through the portal.',
        benefit: 'Organized communication'
      }
    ]
  },
  {
    id: 'agency-dashboard',
    category: 'FOR AGENCY MANAGERS',
    title: 'Run Your Entire Agency From One Dashboard',
    description: 'Stop juggling spreadsheets, emails, and payment links. Manage everything in one beautiful interface.',
    gradient: 'from-violet-500 to-indigo-500',
    features: [
      {
        icon: BarChart3,
        title: 'Revenue Analytics',
        description: 'Track monthly recurring revenue, growth trends, top clients, and popular services at a glance.',
        benefit: 'Data-driven decisions'
      },
      {
        icon: Users,
        title: 'Client CRM',
        description: 'Full client profiles with contact info, service history, revenue generated, and communication logs.',
        benefit: 'Know every client'
      },
      {
        icon: Layers,
        title: 'Service Catalog Builder',
        description: 'Create service packages with tiers, features, and pricing. Organize by category for easy browsing.',
        benefit: 'Professional offerings'
      },
      {
        icon: CreditCard,
        title: 'Stripe Integration',
        description: 'Accept payments instantly. Automatic invoicing, subscription billing, and refund management.',
        benefit: 'Get paid faster'
      },
      {
        icon: Clock,
        title: 'Automated Renewals',
        description: 'System tracks expiration dates and sends reminders. Never lose a renewal again.',
        benefit: 'Predictable revenue'
      },
      {
        icon: PieChart,
        title: 'Business Reports',
        description: 'Weekly and monthly reports delivered to your inbox. Revenue, client growth, service performance.',
        benefit: 'Stay informed'
      }
    ]
  },
  {
    id: 'ai-features',
    category: 'AI-POWERED',
    title: 'Convert Visitors Into Leads With AI',
    description: 'Our intelligent chatbot engages visitors 24/7, answers questions about your services, and captures qualified leads while you sleep.',
    gradient: 'from-cyan-500 to-blue-500',
    features: [
      {
        icon: Bot,
        title: 'AI Sales Assistant',
        description: 'Trained on your services, the chatbot answers questions, recommends packages, and guides visitors to purchase.',
        benefit: 'Sales on autopilot'
      },
      {
        icon: Target,
        title: 'Lead Qualification',
        description: 'After 2 messages, visitors are prompted to share contact info. You get qualified leads, not tire-kickers.',
        benefit: 'Quality over quantity'
      },
      {
        icon: Calendar,
        title: 'Booking Integration',
        description: 'Connect your Calendly or Cal.com. The AI offers to schedule calls, turning interest into meetings.',
        benefit: 'Automated scheduling'
      },
      {
        icon: Mail,
        title: 'Lead Notifications',
        description: 'Get instant email alerts when new leads come in. Never miss a hot prospect.',
        benefit: 'Fast follow-up'
      },
      {
        icon: Sparkles,
        title: 'Smart Responses',
        description: 'Natural, helpful responses that match your brand voice. Visitors feel heard, not automated.',
        benefit: 'Better engagement'
      },
      {
        icon: TrendingUp,
        title: 'Conversation Analytics',
        description: 'See what visitors ask most, where they drop off, and which questions convert best.',
        benefit: 'Optimize continuously'
      }
    ]
  },
  {
    id: 'white-label',
    category: 'WHITE-LABEL READY',
    title: 'Your Brand, Your Domain, Your Portal',
    description: 'Launch a fully branded client portal in minutes. Custom domains, logos, colors—your clients never see "Edrano".',
    gradient: 'from-emerald-500 to-teal-500',
    features: [
      {
        icon: Globe,
        title: 'Custom Domain',
        description: 'Use your own domain (portal.youragency.com). SSL included. Professional from day one.',
        benefit: 'Build your brand'
      },
      {
        icon: Palette,
        title: 'Full Branding Control',
        description: 'Upload your logo, set your colors, customize the hero section. Make it 100% yours.',
        benefit: 'Consistent identity'
      },
      {
        icon: Mail,
        title: 'Branded Emails',
        description: 'Notifications go out from your email address with your branding. Build trust with every message.',
        benefit: 'Professional communication'
      },
      {
        icon: Building2,
        title: 'Multi-Tenant Ready',
        description: 'Running multiple brands? Create separate portals for each, all from one dashboard.',
        benefit: 'Scale without limits'
      },
      {
        icon: Shield,
        title: 'Remove Edrano Branding',
        description: 'Growth and Scale plans let you remove all Edrano references. It\'s 100% your platform.',
        benefit: 'Complete ownership'
      },
      {
        icon: Award,
        title: 'Client Testimonials',
        description: 'Showcase social proof with a built-in testimonial section. Build trust before they buy.',
        benefit: 'Higher conversions'
      }
    ]
  },
  {
    id: 'team-features',
    category: 'TEAM COLLABORATION',
    title: 'Work Together, Stay Organized',
    description: 'Invite your team with role-based permissions. Everyone knows what to do, and nothing falls through the cracks.',
    gradient: 'from-amber-500 to-orange-500',
    features: [
      {
        icon: UserPlus,
        title: 'Team Invitations',
        description: 'Add team members via email invite. They get their own login and role-based access.',
        benefit: 'Easy onboarding'
      },
      {
        icon: Shield,
        title: 'Role-Based Permissions',
        description: 'Admins, managers, support staff—each role sees only what they need. Keep data secure.',
        benefit: 'Security by design'
      },
      {
        icon: Bell,
        title: 'Shared Notifications',
        description: 'Team-wide alerts for new orders, expiring services, and client messages.',
        benefit: 'Stay in sync'
      },
      {
        icon: MessageSquare,
        title: 'Internal Notes',
        description: 'Leave notes on clients and orders. Keep everyone informed about special situations.',
        benefit: 'Better handoffs'
      },
      {
        icon: FolderOpen,
        title: 'Activity Logs',
        description: 'See who did what, when. Full audit trail for accountability and troubleshooting.',
        benefit: 'Complete visibility'
      },
      {
        icon: RefreshCw,
        title: 'Real-Time Updates',
        description: 'Changes sync instantly. Your team always has the latest information.',
        benefit: 'No version conflicts'
      }
    ]
  },
  {
    id: 'integrations',
    category: 'INTEGRATIONS & API',
    title: 'Connect Your Entire Tech Stack',
    description: 'Webhooks, REST API, and native integrations let you connect Edrano to your existing tools.',
    gradient: 'from-slate-600 to-slate-800',
    features: [
      {
        icon: Webhook,
        title: 'Outgoing Webhooks',
        description: '15+ event types including new orders, client signups, service expirations, and payments.',
        benefit: 'Real-time automation'
      },
      {
        icon: Key,
        title: 'REST API',
        description: 'Full CRUD API for clients, services, and orders. Build custom integrations or sync with your systems.',
        benefit: 'Developer-friendly'
      },
      {
        icon: Zap,
        title: 'Zapier Ready',
        description: 'Connect to 5,000+ apps. Automate workflows between Edrano and your favorite tools.',
        benefit: 'No-code automation'
      },
      {
        icon: CreditCard,
        title: 'Stripe Connect',
        description: 'Secure payment processing with Stripe. PCI compliant, global currencies supported.',
        benefit: 'Trusted payments'
      },
      {
        icon: Mail,
        title: 'Email Providers',
        description: 'Use Maileroo, SendGrid, or your own SMTP. Full control over email delivery.',
        benefit: 'Flexible delivery'
      },
      {
        icon: Settings,
        title: 'Incoming Sync API',
        description: 'Push data from external systems into Edrano. Sync clients, orders, and payments.',
        benefit: 'Centralized data'
      }
    ]
  }
];

// ============================================
// COMPONENTS
// ============================================

function FeatureCard({ icon: Icon, title, description, benefit }) {
  return (
    <div className="group p-6 bg-white rounded-2xl border border-stone-200 hover:border-stone-300 hover:shadow-lg transition-all duration-300">
      <div className="w-12 h-12 rounded-xl bg-stone-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
        <Icon className="w-6 h-6 text-stone-700" />
      </div>
      <h3 className="font-semibold text-lg text-stone-900 mb-2">{title}</h3>
      <p className="text-stone-600 text-sm leading-relaxed mb-3">{description}</p>
      <div className="flex items-center gap-2 text-sm">
        <Check className="w-4 h-4 text-emerald-500" />
        <span className="text-emerald-700 font-medium">{benefit}</span>
      </div>
    </div>
  );
}

function FeatureSection({ section, index }) {
  const isEven = index % 2 === 0;
  
  return (
    <section className={`py-20 ${isEven ? 'bg-white' : 'bg-stone-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r ${section.gradient} text-white text-xs font-medium tracking-wider mb-4`}>
            {section.category}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
            {section.title}
          </h2>
          <p className="text-lg text-stone-600">
            {section.description}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {section.features.map((feature, idx) => (
            <FeatureCard key={idx} {...feature} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-stone-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
              <span className="font-semibold">Edrano</span>
            </div>
            <p className="text-stone-400 text-sm">
              Edrano provides the agile financial framework your business needs to evolve without limits.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <div className="space-y-2">
              <Link to="/features" className="block text-stone-400 hover:text-white text-sm">Features</Link>
              <Link to="/pricing" className="block text-stone-400 hover:text-white text-sm">Pricing</Link>
              <Link to="/use-cases" className="block text-stone-400 hover:text-white text-sm">Use Cases</Link>
              <Link to="/compare" className="block text-stone-400 hover:text-white text-sm">Compare</Link>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <div className="space-y-2">
              <Link to="/docs" className="block text-stone-400 hover:text-white text-sm">Documentation</Link>
              <Link to="/docs/quick-start" className="block text-stone-400 hover:text-white text-sm">Getting Started</Link>
              <Link to="/docs/api-auth" className="block text-stone-400 hover:text-white text-sm">API Reference</Link>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <div className="space-y-2">
              <Link to="/privacy" className="block text-stone-400 hover:text-white text-sm">Privacy Policy</Link>
              <Link to="/terms" className="block text-stone-400 hover:text-white text-sm">Terms of Service</Link>
              <Link to="/gdpr" className="block text-stone-400 hover:text-white text-sm">GDPR</Link>
            </div>
          </div>
        </div>
        
        <div className="border-t border-stone-800 pt-8 text-center text-stone-500 text-sm">
          © {new Date().getFullYear()} Edrano. All rights reserved.
          <span className="mx-2">•</span>
          Part of <a href="https://themelio.app" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">Themelio</a>
        </div>
      </div>
    </footer>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function Features() {
  return (
    <div className="min-h-screen bg-white">
      <MorphingNavbar />
      
      {/* Hero */}
      <section className="py-20 bg-gradient-to-b from-stone-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-900 text-white text-sm mb-6">
            <Sparkles className="w-4 h-4" />
            Everything you need to run your agency
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 mb-6">
            Powerful Features,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-pink-500">
              Simple Experience
            </span>
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mb-8">
            Client management, billing, AI lead generation, white-label portals, and team collaboration—all in one platform designed for service agencies.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-stone-900 hover:bg-stone-800">
                Start free trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/app/demo">
              <Button size="lg" variant="outline">
                See live demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-stone-900">30+</div>
              <div className="text-sm text-stone-500">Core Features</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-stone-900">15+</div>
              <div className="text-sm text-stone-500">Webhook Events</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-stone-900">100%</div>
              <div className="text-sm text-stone-500">White-Label</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-stone-900">0%</div>
              <div className="text-sm text-stone-500">Transaction Fees</div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      {FEATURE_SECTIONS.map((section, index) => (
        <FeatureSection key={section.id} section={section} index={index} />
      ))}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-stone-900 to-stone-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to transform your agency?
          </h2>
          <p className="text-lg text-stone-300 mb-8">
            Join hundreds of agencies already using Edrano to manage clients, automate billing, and grow revenue.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-stone-900 hover:bg-stone-100">
                Start your free trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                View pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
